import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MonitorSmartphone, Store, PlayCircle, LockKeyhole, Settings, BarChart3 } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-200 text-slate-600",
  closed: "bg-rose-100 text-rose-700",
};

export default function EposHome() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([api.get("/epos/config"), api.get("/epos/registers")]);
      setCfg(c.data);
      setRegs(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load EPOS");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const storeName = (sid) => (cfg?.stores || []).find((s) => s.id === sid)?.name || "—";

  const openRegister = async (reg) => {
    setBusy(reg.id);
    try {
      if (!reg.open_session) {
        await api.post(`/epos/registers/${reg.id}/open`, { opening_float: 0 });
      }
      navigate(`/epos/register?register=${reg.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open register");
      setBusy("");
    }
  };

  return (
    <div className="w-full px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="epos-home">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <PageIcon /> EPOS
          </h1>
          <p className="text-sm text-slate-500 mt-1">Point of Sale — stores, registers and the sales terminal.</p>
        </div>
        <div className="flex items-center gap-2">
          {cfg?.permissions?.reports && (
            <button onClick={() => navigate("/epos/reports")} data-testid="epos-home-reports-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
              <BarChart3 className="w-4 h-4" /> Reports
            </button>
          )}
          {cfg?.permissions?.manage && (
            <button onClick={() => navigate("/epos/settings")} data-testid="epos-home-settings-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
              <Settings className="w-4 h-4" /> Settings
            </button>
          )}
        </div>
      </div>

      {!cfg?.permissions?.sell && !loading && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 mb-6 flex items-center gap-2">
          <LockKeyhole className="w-4 h-4" /> You can view EPOS but do not have permission to open a register or take sales.
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-16 text-center">Loading registers…</div>
      ) : regs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-slate-500" data-testid="epos-home-empty">
          <Store className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          No registers configured yet.
          {cfg?.permissions?.manage && <div className="mt-2 text-sm">Add a store and register in <button className="text-indigo-600 underline" onClick={() => navigate("/epos/settings")}>Settings</button>.</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="epos-register-list">
          {regs.map((reg) => (
            <div key={reg.id} data-testid={`epos-register-card-${reg.id}`}
              className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                    <Store className="w-3.5 h-3.5" /> {storeName(reg.store_id)}
                  </div>
                  <div className="text-lg font-bold text-slate-900 mt-1">{reg.name}</div>
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md uppercase ${STATUS_STYLE[reg.status] || STATUS_STYLE.inactive}`}>
                  {reg.status}
                </span>
              </div>
              <div className="text-sm text-slate-500">
                {reg.open_session
                  ? <span className="text-emerald-600 font-medium">Session open · by {reg.open_session.opened_by_name}</span>
                  : <span>No open session</span>}
              </div>
              <button
                disabled={reg.status !== "active" || !cfg?.permissions?.sell || busy === reg.id}
                onClick={() => openRegister(reg)}
                data-testid={`epos-open-register-${reg.id}`}
                className="mt-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
                <PlayCircle className="w-4 h-4" />
                {reg.open_session ? "Enter register" : "Open register"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
